import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Award, Calendar, DollarSign, Mountain, Mail, Phone, User, Send, Download } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { generatePersona } from '../../data/adventureData';

const FinalMasterpiece = ({ adventureData, onSubmit }) => {
  const { toast } = useToast();
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { journey, totalDays, totalCost, difficulty, choicesSummary, profileAnswers, customItinerary } = adventureData;
  const personaData = generatePersona(profileAnswers);

  const handleChange = (e) => {
    setContactInfo({
      ...contactInfo,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const fullData = {
      ...adventureData,
      contactInfo,
      persona: personaData
    };

    try {
      await onSubmit(fullData);
      
      // Clear form
      setContactInfo({
        name: '',
        email: '',
        phone: ''
      });
      
      // Show success message
      toast({
        title: "✅ Submission Successful!",
        description: "Thank you for using our Adventure Builder! Your custom itinerary has been sent to our team. We'll get back to you within 24 hours with a detailed quote.",
        duration: 6000,
      });
      
      // Scroll to top to show success message clearly
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: "Submission Error",
        description: "Something went wrong while sending your itinerary. Please try again or contact us directly at info@trailz.in",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDifficultyLabel = () => {
    if (difficulty <= 1.5) return 'Easy';
    if (difficulty <= 2.5) return 'Moderate';
    if (difficulty <= 3.5) return 'Challenging';
    return 'Demanding';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white py-20 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 mb-6">
            <Award className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-serif mb-6 text-zinc-900">
            Your Masterpiece is Complete
          </h1>
          <p className="text-xl text-zinc-600 max-w-2xl mx-auto">
            Behold your handcrafted {journey.name} - designed perfectly for you
          </p>
        </div>

        {/* Persona Badge */}
        <Card className="mb-12 overflow-hidden border-0 shadow-2xl rounded-sm">
          <div className="bg-gradient-to-r from-emerald-800 to-emerald-900 p-8 text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <Mountain className="w-8 h-8" />
              </div>
              <div>
                <p className="text-sm text-white/80 mb-1">Your Adventurer Persona</p>
                <h2 className="text-3xl font-serif">{personaData.persona}</h2>
              </div>
            </div>
            <p className="text-white/90 text-lg">{personaData.description}</p>
          </div>
        </Card>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className="border-0 shadow-lg rounded-sm">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-6 h-6 text-blue-700" />
              </div>
              <p className="text-sm text-zinc-600 mb-1">Total Duration</p>
              <p className="text-3xl font-bold text-zinc-900">{totalDays} Days</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg rounded-sm">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-6 h-6 text-amber-700" />
              </div>
              <p className="text-sm text-zinc-600 mb-1">Total Investment</p>
              <p className="text-3xl font-bold text-zinc-900">${totalCost}</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg rounded-sm">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <Mountain className="w-6 h-6 text-red-700" />
              </div>
              <p className="text-sm text-zinc-600 mb-1">Difficulty Level</p>
              <p className="text-3xl font-bold text-zinc-900">{getDifficultyLabel()}</p>
            </CardContent>
          </Card>
        </div>

        {/* Complete Itinerary */}
        <Card className="mb-12 border-0 shadow-lg rounded-sm">
          <CardContent className="p-8">
            <h3 className="text-2xl font-serif mb-6 text-zinc-900">Your Complete Itinerary</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {(customItinerary || journey.baseItinerary).map((item, idx) => (
                <div key={idx} className={`flex items-start gap-4 p-4 rounded-sm ${item.isCustom ? 'bg-amber-50 border-2 border-amber-300' : 'bg-zinc-50 border border-zinc-200'}`}>
                  <div className={`w-10 h-10 rounded-full text-white flex items-center justify-center text-sm font-semibold flex-shrink-0 ${item.isCustom ? 'bg-amber-600' : 'bg-emerald-700'}`}>
                    {item.day || idx + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-zinc-900">{item.title}</p>
                    {item.description && <p className="text-sm text-zinc-600 mt-1">{item.description}</p>}
                    {item.isCustom && (
                      <span className="inline-block mt-2 text-xs text-amber-700 font-semibold bg-amber-100 px-2 py-1 rounded">
                        Custom Addition
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Your Choices */}
        {choicesSummary.length > 0 && (
          <Card className="mb-12 border-0 shadow-lg rounded-sm">
            <CardContent className="p-8">
              <h3 className="text-2xl font-serif mb-6 text-zinc-900">Your Custom Choices</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {choicesSummary.map((choice, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-emerald-700 text-sm font-semibold">{idx + 1}</span>
                    </div>
                    <span className="text-zinc-700">{choice}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contact Form */}
        <Card className="border-2 border-emerald-700 shadow-2xl rounded-sm">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-serif mb-3 text-zinc-900">
                Get Your Full Quote & Itinerary
              </h3>
              <p className="text-zinc-600">
                Share your details and we'll send you a comprehensive quote within 24 hours
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-zinc-700 font-medium mb-2 block">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Full Name *
                  </div>
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={contactInfo.name}
                  onChange={handleChange}
                  required
                  className="border-zinc-300 focus:border-emerald-700 rounded-sm py-6"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-zinc-700 font-medium mb-2 block">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Address *
                  </div>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={contactInfo.email}
                  onChange={handleChange}
                  required
                  className="border-zinc-300 focus:border-emerald-700 rounded-sm py-6"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-zinc-700 font-medium mb-2 block">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Phone Number *
                  </div>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={contactInfo.phone}
                  onChange={handleChange}
                  required
                  className="border-zinc-300 focus:border-emerald-700 rounded-sm py-6"
                  placeholder="+1 (234) 567-8900"
                />
              </div>

              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-emerald-800 to-emerald-900 hover:from-emerald-900 hover:to-emerald-950 text-white py-7 text-lg rounded-sm transition-all duration-300 shadow-xl"
              >
                {isSubmitting ? 'Sending Your Adventure...' : 'Send My Itinerary & Get Full Quote'}
                <Send className="ml-2 w-5 h-5" />
              </Button>
            </form>

            <div className="mt-6 p-4 bg-amber-50 rounded-sm border border-amber-200">
              <p className="text-sm text-amber-900 text-center">
                🎉 Congratulations! You've crafted a truly unique adventure. Our team will review your masterpiece and send you a detailed quote.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FinalMasterpiece;